import React, { useCallback, useRef, useState } from "react";

import EventService from "service/event/EventService";
import PatientService from "../service/PatientService";
import ProfileDetailsDialog, { isDOBEmpty, isEmailEmpty } from "./ProfileDetailsDialog";
import RescheduleModal from "./RescheduleModal";

/**
 * What a Reschedule button does — bridge's startRescheduleFlow, shared by both
 * phr entry points (appointment details, and the patient header).
 *
 * Bridge does not open the dialog straight away: it re-reads the MEMBER's
 * patient record (bookedFor, or patientID when bookedFor is 0) and, when the
 * date of birth or the email is missing, shows "Update missing profile details"
 * first. Only a record with both — or a successful save — opens "Reschedule
 * appointment?". A failed read toasts `Error: …` and opens nothing; a record
 * with no id opens nothing, silently.
 *
 * Returns the click handler and the dialogs to render.
 */
const useRescheduleFlow = ({ appointment, onRescheduled }) => {
  const patientService = useRef(new PatientService()).current;
  const [profilePatient, setProfilePatient] = useState(null);
  const [open, setOpen] = useState(false);

  const startReschedule = useCallback(() => {
    if (!appointment || appointment.patientID === null || appointment.patientID === undefined) return;
    const pid = appointment.bookedFor === 0 ? appointment.patientID : appointment.bookedFor;
    patientService.fetchPatient(
      pid,
      (res) => {
        if (res && res.id !== undefined) {
          if (isDOBEmpty(res) || isEmailEmpty(res)) {
            setProfilePatient(res);
          } else {
            setProfilePatient(null);
            setOpen(true);
          }
        }
      },
      (err) => {
        setProfilePatient(null);
        EventService.emit("showError", {
          message: `Error: ${err === null || err === undefined ? undefined : err.message}`,
        });
      }
    );
  }, [appointment, patientService]);

  const rescheduleUI = (
    <>
      {profilePatient && (
        <ProfileDetailsDialog
          patient={profilePatient}
          onClose={() => setProfilePatient(null)}
          onSaved={() => {
            setProfilePatient(null);
            setOpen(true);
          }}
        />
      )}
      {open && appointment && (
        <RescheduleModal
          appointment={appointment}
          onClose={() => setOpen(false)}
          onRescheduled={onRescheduled}
        />
      )}
    </>
  );

  return { startReschedule, rescheduleUI };
};

export default useRescheduleFlow;
